import { supabase } from "@/lib/supabase";
import { fireEvent } from "@/lib/events";
import { invalidateActiveSpaceRoleCache } from "@/hooks/useAuth";
import { queryClient } from "@/lib/queryClient";

/**
 * Switch the user's active space:
 *   1. UPDATE profiles.active_space_id = spaceId.
 *   2. Invalidate TanStack Query caches for space-scoped hooks.
 *   3. Fire `space.switched` so remaining subscribers (useAuth) refresh.
 *
 * Callers handle navigation + any UI side effects (toast, closing the
 * switcher popover, etc.) — this function is orchestration only.
 *
 * Returns `true` on success, `false` on error. No exception is thrown so
 * the caller can surface a toast without needing a try/catch.
 */
export async function switchSpace(userId: string, spaceId: string): Promise<boolean> {
  const { error } = await supabase
    .from("profiles")
    .update({ active_space_id: spaceId })
    .eq("id", userId);

  if (error) {
    console.error("[spaceSwitch] profiles update failed:", error.message);
    return false;
  }

  invalidateActiveSpaceRoleCache(userId);
  queryClient.invalidateQueries({ queryKey: ["active-space", userId] });
  queryClient.invalidateQueries({ queryKey: ["spaces", userId] });
  queryClient.invalidateQueries({ queryKey: ["current-role", userId] });

  fireEvent("space.switched", { userId, spaceId });
  return true;
}
